import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const TABS = [
  { id: 'overview', name: 'Overview', icon: '📊' },
  { id: 'external', name: 'External APIs', icon: '🌐' },
  { id: 'limits', name: 'Rate Limits', icon: '⚡' },
  { id: 'internal', name: 'Internal APIs', icon: '🔌' },
  { id: 'processes', name: 'Processes', icon: '⚙️' },
];

function UtilBar({ pct }) {
  const p = Math.min(100, Math.max(0, Number(pct) || 0));
  const safeColor = p >= 90 ? 'bg-red-500' : p >= 70 ? 'bg-yellow-500' : 'bg-blue-500';
  return (
    <div className="w-full h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
      <div className={`h-full ${safeColor} transition-all duration-500`} style={{ width: `${p}%` }} />
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    healthy: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    idle: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    degraded: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
    down: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
    unconfigured: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
    unknown: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
  };
  const label = status === 'healthy' ? 'Healthy' : status === 'idle' ? 'Idle'
    : status === 'degraded' ? 'Degraded' : status === 'down' ? 'Down'
      : status === 'unconfigured' ? 'Not configured' : 'Unknown';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${map[status] || map.unknown}`}>
      {label}
    </span>
  );
}

function OverallBadge({ status }) {
  const map = {
    healthy: { bg: 'bg-green-500', text: 'All systems operational' },
    degraded: { bg: 'bg-yellow-500', text: 'Some issues detected' },
    down: { bg: 'bg-red-500', text: 'Critical services down' },
  };
  const cfg = map[status] || map.degraded;
  return (
    <div className="flex items-center gap-2">
      <span className={`w-2.5 h-2.5 rounded-full ${cfg.bg} animate-pulse`} />
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{cfg.text}</span>
    </div>
  );
}

function ApiCard({ title, subtitle, children, className = '' }) {
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm ${className}`}>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
        {subtitle ? <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p> : null}
      </div>
      {children}
    </div>
  );
}

function StatRow({ label, value, mono, hint }) {
  return (
    <div className="py-1.5 text-sm">
      <div className="flex items-center justify-between">
        <span className="text-gray-500 dark:text-gray-400">{label}</span>
        <span className={`font-medium text-gray-900 dark:text-gray-100 ${mono ? 'font-mono' : ''}`}>{value}</span>
      </div>
      {hint ? <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{hint}</p> : null}
    </div>
  );
}

function KpiCard({ label, value, sub, tone = 'blue' }) {
  const tones = {
    blue: 'border-blue-200 dark:border-blue-800',
    green: 'border-green-200 dark:border-green-800',
    amber: 'border-amber-200 dark:border-amber-800',
    red: 'border-red-200 dark:border-red-800',
    purple: 'border-purple-200 dark:border-purple-800',
  };
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border ${tones[tone]} p-4 shadow-sm`}>
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1 font-mono">{value}</p>
      {sub ? <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{sub}</p> : null}
    </div>
  );
}

function ageLabel(updatedAt) {
  if (!updatedAt) return '—';
  const sec = Math.max(0, Math.round((Date.now() - new Date(updatedAt).getTime()) / 1000));
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  return `${Math.floor(sec / 60)}m ago`;
}

function formatUptime(ms) {
  if (!ms) return '—';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
  return `${Math.floor(sec / 86400)}d ${Math.floor((sec % 86400) / 3600)}h`;
}

function ProcessPanel({ name, data, roleHint }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  if (!data) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400 italic">
        No snapshot from <span className="font-mono">{name}</span> process yet.
        <p className="mt-2 not-italic text-xs">Check that PM2 process <span className="font-mono">cookbot{name === 'bot' ? '' : '-worker'}</span> is online.</p>
      </div>
    );
  }

  const dex = data.dexscreener || {};
  const gecko = data.geckoterminal || {};
  const feed = data.priceFeed || {};
  const cache = data.cache || {};
  const geckoIdle = (gecko.requestsLastMin ?? 0) === 0;

  return (
    <div className="space-y-4">
      {roleHint ? (
        <p className="text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg px-3 py-2">
          {roleHint}
        </p>
      ) : null}

      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">DexScreener</span>
          <StatusPill status={dex.rateLimited ? 'degraded' : 'healthy'} />
        </div>
        <UtilBar pct={dex.utilizationPct} />
        <div className="mt-2 space-y-0">
          <StatRow label="Usage (rolling 60s)" value={`${dex.requestsLastMin ?? 0} / ${dex.limitPerMin ?? 300} req`} mono />
          <StatRow label="Queue" value={dex.queueLength ?? 0} mono />
          <StatRow label="429 (last hour)" value={dex.recent429Count ?? 0} mono />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            GeckoTerminal {gecko.pro ? '(Pro)' : '(Free)'}
          </span>
          <StatusPill status={geckoIdle ? 'idle' : 'healthy'} />
        </div>
        <UtilBar pct={gecko.utilizationPct} />
        <div className="mt-2">
          <StatRow label="Usage (rolling 60s)" value={`${gecko.requestsLastMin ?? 0} / ${gecko.limitPerMin ?? 30} req`} mono />
        </div>
      </div>

      {feed.running ? (
        <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Central price feed</p>
          <StatRow label="Unique tokens" value={feed.registrySize ?? 0} mono />
          <StatRow label="Batch HTTP calls" value={feed.batchPolls ?? 0} mono />
          <StatRow label="Poll errors" value={feed.errors ?? 0} mono />
        </div>
      ) : name === 'bot' ? (
        <div className="pt-2 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-400">
          Price feed runs on the worker process.
        </div>
      ) : null}

      <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
        <StatRow label="Cache hit rate" value={cache.hitRate ?? '—'} mono />
        <StatRow label="Cache entries" value={cache.cacheSize ?? 0} mono />
      </div>

      <p className="text-xs text-gray-400">
        Snapshot {ageLabel(data.updatedAt)}
        {data.snapshotAgeSec != null ? ` (${data.snapshotAgeSec}s old)` : ''}
      </p>
    </div>
  );
}

function OverviewTab({ data }) {
  const s = data?.summary || {};
  const analytics = data?.analytics || {};
  const combined = data?.rateLimits?.combined;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
        <OverallBadge status={data?.overall} />
        <span className="text-xs text-gray-400">
          Last checked {data?.checkedAt ? new Date(data.checkedAt).toLocaleTimeString() : '—'}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Tokens tracked" value={s.tokensTracked?.toLocaleString() ?? '—'} sub="Active (not paused)" tone="blue" />
        <KpiCard label="Active users" value={s.activeUsers?.toLocaleString() ?? '—'} sub="Telegram accounts" tone="green" />
        <KpiCard label="Recovery alerts (24h)" value={s.recoveryAlerts24h?.toLocaleString() ?? '—'} sub="Events fired" tone="purple" />
        <KpiCard label="Price feed tokens" value={s.registrySize?.toLocaleString() ?? '—'} sub={s.priceFeedRunning ? 'Feed running' : 'Feed stopped'} tone="amber" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ApiCard title="External services" subtitle={`${s.externalOperational ?? 0} / ${s.externalTotal ?? 0} operational`}>
          <div className="space-y-2">
            <StatRow label="Issues" value={s.externalIssues ?? 0} />
            <StatRow label="DexScreener load" value={`${s.dexUtilization ?? 0}%`} mono />
            <StatRow label="GeckoTerminal load" value={`${s.geckoUtilization ?? 0}%`} mono />
            <StatRow label="Dex 429 (last hour)" value={analytics.dex429LastHour ?? 0} mono />
          </div>
        </ApiCard>

        <ApiCard title="Internal APIs" subtitle={`${s.internalHealthy ?? 0} / ${s.internalTotal ?? 0} healthy`}>
          <div className="space-y-2">
            <StatRow label="Endpoints down" value={s.internalDown ?? 0} />
            <StatRow label="Critical down" value={s.criticalDown ?? 0} tone={s.criticalDown > 0 ? 'red' : undefined} />
          </div>
        </ApiCard>

        <ApiCard title="PM2 processes" subtitle={`${s.pm2Online ?? 0} online`}>
          <div className="space-y-2">
            {s.pm2Missing?.length > 0 ? (
              <p className="text-sm text-red-600 dark:text-red-400">
                Missing: {s.pm2Missing.join(', ')}
              </p>
            ) : (
              <p className="text-sm text-green-600 dark:text-green-400">All core processes online</p>
            )}
            <StatRow label="Cache hit rate" value={s.cacheHitRate ?? '—'} mono />
          </div>
        </ApiCard>
      </div>

      {combined ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ApiCard title="DexScreener (live)" subtitle="Combined worker + bot">
            <div className="flex items-center justify-between mb-3">
              <StatusPill status={combined.dexscreener?.rateLimited ? 'degraded' : 'healthy'} />
              <span className="text-2xl font-bold font-mono">{combined.dexscreener?.utilizationPct ?? 0}%</span>
            </div>
            <UtilBar pct={combined.dexscreener?.utilizationPct} />
            <div className="mt-3">
              <StatRow label="Requests/min" value={`${combined.dexscreener?.requestsLastMin ?? 0} / 300`} mono />
              <StatRow label="Queued" value={combined.dexscreener?.queueLength ?? 0} mono />
            </div>
          </ApiCard>
          <ApiCard title="GeckoTerminal (live)" subtitle={combined.geckoterminal?.pro ? 'Pro tier' : 'Free tier'}>
            <div className="flex items-center justify-between mb-3">
              <StatusPill status={(combined.geckoterminal?.requestsLastMin ?? 0) === 0 ? 'idle' : 'healthy'} />
              <span className="text-2xl font-bold font-mono">{combined.geckoterminal?.utilizationPct ?? 0}%</span>
            </div>
            <UtilBar pct={combined.geckoterminal?.utilizationPct} />
            <div className="mt-3">
              <StatRow label="Requests/min" value={`${combined.geckoterminal?.requestsLastMin ?? 0} / ${combined.geckoterminal?.limitPerMin ?? 250}`} mono />
            </div>
          </ApiCard>
        </div>
      ) : null}

      {analytics.db ? (
        <ApiCard title="Database snapshot" subtitle="MongoDB collections">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatRow label="Tokens" value={analytics.db.tokens?.toLocaleString()} mono />
            <StatRow label="Recoveries" value={analytics.db.recoveries?.toLocaleString()} mono />
            <StatRow label="Groups" value={analytics.db.groups?.toLocaleString()} mono />
            <StatRow label="Users" value={analytics.db.users?.toLocaleString()} mono />
          </div>
        </ApiCard>
      ) : null}
    </div>
  );
}

function ExternalTab({ services }) {
  if (!services?.length) return <p className="text-gray-500">No external services configured.</p>;

  const byCategory = services.reduce((acc, svc) => {
    const cat = svc.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(svc);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {Object.entries(byCategory).map(([category, items]) => (
        <div key={category}>
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">{category}</h3>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Service</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400 hidden md:table-cell">Details</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400 hidden lg:table-cell">Metrics</th>
                </tr>
              </thead>
              <tbody>
                {items.map((svc) => (
                  <tr key={svc.id} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900 dark:text-white">{svc.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{svc.description}</p>
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill status={svc.status} />
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-gray-600 dark:text-gray-400 text-xs">
                      {svc.configured ? 'Configured' : 'Not configured'}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-xs font-mono text-gray-600 dark:text-gray-400">
                      {[
                        svc.metrics?.utilizationPct != null && `${svc.metrics.utilizationPct}% util`,
                        svc.metrics?.latencyMs != null && `${svc.metrics.latencyMs}ms`,
                        svc.metrics?.requestsLastMin != null && `${svc.metrics.requestsLastMin} req/min`,
                        svc.metrics?.tokens != null && `${svc.metrics.tokens} tokens`,
                        svc.metrics?.model && svc.metrics.model,
                        svc.metrics?.activeProvider === true && 'active provider',
                        svc.metrics?.activeProvider === false && svc.metrics?.reportProvider && `pref: ${svc.metrics.reportProvider}`,
                      ].filter(Boolean).join(' · ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

function InternalTab({ endpoints }) {
  if (!endpoints?.length) return <p className="text-gray-500">No internal endpoints checked.</p>;

  const byGroup = endpoints.reduce((acc, ep) => {
    const g = ep.group || 'Other';
    if (!acc[g]) acc[g] = [];
    acc[g].push(ep);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {Object.entries(byGroup).map(([group, items]) => (
        <div key={group}>
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">{group}</h3>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Endpoint</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Latency</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400 hidden md:table-cell">Path</th>
                </tr>
              </thead>
              <tbody>
                {items.map((ep) => (
                  <tr key={ep.path} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900 dark:text-white">{ep.name}</span>
                      {ep.critical ? (
                        <span className="ml-2 text-[10px] uppercase bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 px-1.5 py-0.5 rounded">critical</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill status={ep.status} />
                    </td>
                    <td className="px-4 py-3 font-mono text-gray-600 dark:text-gray-400">
                      {ep.latencyMs != null ? `${ep.latencyMs}ms` : '—'}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell font-mono text-xs text-gray-500">{ep.path}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

function LimitsTab({ data }) {
  const combined = data?.rateLimits?.combined;
  const geckoCombined = combined?.geckoterminal || {};
  const geckoIdle = (geckoCombined.requestsLastMin ?? 0) === 0;

  return (
    <div className="space-y-6">
      {combined ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ApiCard title="DexScreener (combined)" subtitle="Shared ~300 req/min per server IP">
            <div className="flex items-center justify-between mb-3">
              <StatusPill status={combined.dexscreener?.rateLimited ? 'degraded' : 'healthy'} />
              <span className="text-2xl font-bold font-mono">{combined.dexscreener?.utilizationPct ?? 0}%</span>
            </div>
            <UtilBar pct={combined.dexscreener?.utilizationPct} />
            <div className="mt-4 space-y-0">
              <StatRow label="Requests (rolling 60s)" value={`${combined.dexscreener?.requestsLastMin ?? 0} / ${combined.dexscreener?.limitPerMin ?? 300}`} mono />
              <StatRow label="Queued" value={combined.dexscreener?.queueLength ?? 0} mono />
              <StatRow label="429 errors (last hour)" value={combined.dexscreener?.recent429Count ?? 0} mono />
              <StatRow label="Worker share" value={`${data?.rateLimits?.worker?.dexscreener?.requestsLastMin ?? 0} req/min`} mono />
              <StatRow label="Bot share" value={`${data?.rateLimits?.bot?.dexscreener?.requestsLastMin ?? 0} req/min`} mono />
            </div>
          </ApiCard>

          <ApiCard title="GeckoTerminal (combined)" subtitle={geckoCombined.pro ? 'CoinGecko Pro onchain' : 'Public API'}>
            <div className="flex items-center justify-between mb-3">
              <StatusPill status={geckoIdle ? 'idle' : 'healthy'} />
              <span className="text-2xl font-bold font-mono">{geckoCombined.utilizationPct ?? 0}%</span>
            </div>
            <UtilBar pct={geckoCombined.utilizationPct} />
            <div className="mt-4">
              <StatRow label="Requests (rolling 60s)" value={`${geckoCombined.requestsLastMin ?? 0} / ${geckoCombined.limitPerMin ?? 250}`} mono />
              <StatRow label="Worker / Bot" value={`${data?.rateLimits?.worker?.geckoterminal?.requestsLastMin ?? 0} / ${data?.rateLimits?.bot?.geckoterminal?.requestsLastMin ?? 0} req/min`} mono />
            </div>
          </ApiCard>
        </div>
      ) : (
        <p className="text-gray-500">No rate limit snapshots available.</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ApiCard title="Worker process" subtitle="Batch price feed · recovery · scanners">
          <ProcessPanel name="worker" data={data?.rateLimits?.worker} roleHint="Owns background Dex polling via batch API." />
        </ApiCard>
        <ApiCard title="Bot process" subtitle="Telegram · interactive scans · charts">
          <ProcessPanel name="bot" data={data?.rateLimits?.bot} roleHint="Dex/Gecko here are user-triggered only." />
        </ApiCard>
      </div>
    </div>
  );
}

function ProcessesTab({ processes }) {
  if (!processes?.length) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400">
        No PM2 processes found. Ensure PM2 is running on the server.
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Process</th>
            <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Status</th>
            <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">PID</th>
            <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Uptime</th>
            <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Restarts</th>
            <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400 hidden md:table-cell">Memory</th>
            <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400 hidden md:table-cell">CPU</th>
          </tr>
        </thead>
        <tbody>
          {processes.map((p) => (
            <tr key={p.name} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
              <td className="px-4 py-3 font-medium text-gray-900 dark:text-white font-mono">{p.name}</td>
              <td className="px-4 py-3">
                <StatusPill status={p.status === 'online' ? 'healthy' : p.status === 'stopped' ? 'down' : 'degraded'} />
              </td>
              <td className="px-4 py-3 font-mono text-gray-600 dark:text-gray-400">{p.pid ?? '—'}</td>
              <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{formatUptime(p.uptimeMs)}</td>
              <td className="px-4 py-3 font-mono text-gray-600 dark:text-gray-400">{p.restarts ?? 0}</td>
              <td className="px-4 py-3 hidden md:table-cell font-mono text-gray-600 dark:text-gray-400">
                {p.memoryMb != null ? `${p.memoryMb} MB` : '—'}
              </td>
              <td className="px-4 py-3 hidden md:table-cell font-mono text-gray-600 dark:text-gray-400">{p.cpu ?? 0}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ApiStatus() {
  const [activeTab, setActiveTab] = useState('overview');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const res = await api.get('/system/api-overview');
      setData(res.data);
      setLastFetch(new Date());
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load API overview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="flex gap-6 p-6 max-w-7xl mx-auto">
      <div className="w-52 flex-shrink-0 hidden lg:block">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4 sticky top-4">
          <h2 className="font-bold text-gray-900 dark:text-white mb-1">System API</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Services, limits &amp; health</p>
          <div className="space-y-1">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <span>{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="lg:hidden flex gap-1 overflow-x-auto mb-4 pb-1">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {TABS.find((t) => t.id === activeTab)?.name || 'System API'}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Live status for external APIs, internal routes, and PM2 processes
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>

        {loading && !data ? (
          <div className="text-gray-500 dark:text-gray-400">Loading…</div>
        ) : null}

        {error ? (
          <div className="mb-4 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        ) : null}

        {data ? (
          <>
            {activeTab === 'overview' && <OverviewTab data={data} />}
            {activeTab === 'external' && <ExternalTab services={data.externalServices} />}
            {activeTab === 'limits' && <LimitsTab data={data} />}
            {activeTab === 'internal' && <InternalTab endpoints={data.internalEndpoints} />}
            {activeTab === 'processes' && <ProcessesTab processes={data.processes} />}
          </>
        ) : null}

        <p className="mt-6 text-xs text-gray-400 dark:text-gray-500">
          Auto-refreshes every 15s.
          {lastFetch ? ` UI loaded ${lastFetch.toLocaleTimeString()}.` : ''}
        </p>
      </div>
    </div>
  );
}
